import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Chip, Divider, Paper, Typography } from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import { formatMoney } from '../../helpers/invoiceHelpers';
import RefundSummary from './RefundSummary';
import { keys } from './constants';

// The async POST returns no body, so this shows what was submitted (the confirm-time
// snapshot) marked "pending confirmation", not a server result.
const styles = ({ spacing: { unit }, palette }) => ({
  card: {
    padding: unit * 3,
    marginBottom: unit * 3,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: `${unit * 2}px`,
  },
  successIcon: {
    fontSize: 44,
    color: palette.primary.main,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: `${unit * 1.5}px`,
  },
  pendingChip: {
    height: unit * 3,
    color: '#8a6d00',
    backgroundColor: '#fff4d6',
  },
  divider: {
    marginTop: unit * 2,
    marginBottom: unit * 2,
  },
  fields: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${unit * 6}px`,
  },
  overline: {
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    display: 'block',
  },
  info: {
    padding: unit * 2,
    backgroundColor: '#e8f4fd',
    color: '#0b3c5d',
  },
  numeric: {
    fontVariantNumeric: 'tabular-nums',
  },
});

const SubmissionReceipt = ({ classes, translate, submitted, entityName, pspReference, locale, currency }) => {
  const { summary, note } = submitted || {};

  const field = (label, value) => (
    <div>
      <Typography variant="caption" color="textSecondary" className={classes.overline}>
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  );

  return (
    <div>
      <Paper className={classes.card}>
        <div className={classes.header}>
          <CheckCircle className={classes.successIcon} />
          <div>
            <div className={classes.titleRow}>
              <Typography variant="title">{translate(keys.submittedTitle)}</Typography>
              <Chip className={classes.pendingChip} label={translate(keys.submittedPending)} />
            </div>
            {entityName && (
              <Typography variant="body2" color="textSecondary">
                {entityName}
              </Typography>
            )}
          </div>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.fields}>
          {pspReference && field(translate(keys.pspReference), pspReference)}
          <div>
            <Typography variant="caption" color="textSecondary" className={classes.overline}>
              {translate(keys.totalSubmitted)}
            </Typography>
            <Typography variant="title" className={classes.numeric}>
              {formatMoney(summary ? summary.totalTTC : 0, locale, currency)}
            </Typography>
          </div>
          {field(translate(keys.fieldNote), note || '-')}
        </div>
      </Paper>

      {summary && (
        <Paper className={classes.card}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {translate(keys.submittedBreakdownTitle)}
          </Typography>
          <RefundSummary translate={translate} summary={summary} locale={locale} currency={currency} />
        </Paper>
      )}

      <Paper className={classes.info} elevation={0}>
        <Typography variant="body2" color="inherit">
          {translate(keys.submittedNext)}
        </Typography>
      </Paper>
    </div>
  );
};

SubmissionReceipt.propTypes = {
  classes: PropTypes.shape({}),
  translate: PropTypes.func.isRequired,
  submitted: PropTypes.shape({
    summary: PropTypes.shape({}),
    note: PropTypes.string,
  }),
  entityName: PropTypes.string,
  pspReference: PropTypes.string,
  locale: PropTypes.string,
  currency: PropTypes.string,
};

export const SubmissionReceiptTest = SubmissionReceipt;

export default withStyles(styles)(SubmissionReceipt);
